<template>
  <div class="feedback-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>意见与反馈</span>
        </div>
      </template>

      <!-- 查询条件 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="反馈类型">
          <el-select v-model="queryParams.type" placeholder="全部" clearable style="width: 150px">
            <el-option label="功能建议" value="SUGGESTION" />
            <el-option label="问题反馈" value="BUG" />
            <el-option label="数据异常" value="DATA" />
            <el-option label="其他" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="处理状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 150px">
            <el-option label="未处理" value="NEW" />
            <el-option label="处理中" value="PROCESSING" />
            <el-option label="已处理" value="DONE" />
          </el-select>
        </el-form-item>
        <el-form-item label="用户类型">
          <el-select v-model="queryParams.userType" placeholder="全部" clearable style="width: 150px">
            <el-option label="业务员" value="SALESMAN" />
            <el-option label="管理员" value="MANAGER" />
            <el-option label="会计" value="ACCOUNTANT" />
          </el-select>
        </el-form-item>
        <el-form-item label="来源">
          <el-select v-model="queryParams.clientType" placeholder="全部" clearable style="width: 150px">
            <el-option label="业务员小程序" value="MINI_SALESMAN" />
            <el-option label="管理员小程序" value="MINI_MANAGER" />
            <el-option label="PC端" value="PC" />
          </el-select>
        </el-form-item>
        <el-form-item label="关键字">
          <el-input
            v-model="queryParams.keyword"
            placeholder="内容/联系方式"
            clearable
            style="width: 220px"
            @keyup.enter="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="userName" label="反馈用户" width="140" />
        <el-table-column prop="userType" label="用户类型" width="100">
          <template #default="{ row }">
            {{ getUserTypeLabel(row.userType) }}
          </template>
        </el-table-column>
        <el-table-column prop="clientType" label="来源" width="130">
          <template #default="{ row }">
            {{ getClientTypeLabel(row.clientType) }}
          </template>
        </el-table-column>
        <el-table-column prop="type" label="反馈类型" width="120">
          <template #default="{ row }">
            {{ getTypeLabel(row.type) }}
          </template>
        </el-table-column>
        <el-table-column prop="contact" label="联系方式" width="140" />
        <el-table-column prop="content" label="反馈内容" min-width="260" show-overflow-tooltip />
        <el-table-column prop="status" label="处理状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)">
              {{ getStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="feedbackTime" label="反馈时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.feedbackTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link size="small" @click="handleView(row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 详情抽屉 -->
    <el-drawer
      v-model="detailVisible"
      title="反馈详情"
      size="520px"
    >
      <el-descriptions
        v-if="currentDetail"
        :column="1"
        border
        label-class-name="desc-label"
      >
        <el-descriptions-item label="ID">
          {{ currentDetail.id }}
        </el-descriptions-item>
        <el-descriptions-item label="反馈时间">
          {{ formatDateTime(currentDetail.feedbackTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="反馈用户">
          {{ currentDetail.userName }}（{{ getUserTypeLabel(currentDetail.userType) }}）
        </el-descriptions-item>
        <el-descriptions-item label="来源">
          {{ getClientTypeLabel(currentDetail.clientType) }}
        </el-descriptions-item>
        <el-descriptions-item label="类型">
          {{ getTypeLabel(currentDetail.type) }}
        </el-descriptions-item>
        <el-descriptions-item label="联系方式">
          {{ currentDetail.contact }}
        </el-descriptions-item>
        <el-descriptions-item label="处理状态">
          <el-tag :type="getStatusTagType(currentDetail.status)">
            {{ getStatusLabel(currentDetail.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="反馈内容">
          <div class="content-text">
            {{ currentDetail.content }}
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="反馈图片">
          <div v-if="imageUrls.length" class="image-preview-list">
            <el-image
              v-for="(url, index) in imageUrls"
              :key="index"
              :src="url"
              :preview-src-list="imageUrls"
              fit="cover"
              style="width: 80px; height: 80px; margin-right: 8px; border-radius: 4px;"
              crossorigin="anonymous"
              referrerpolicy="no-referrer-when-downgrade"
            />
          </div>
          <span v-else style="color: #999;">无</span>
        </el-descriptions-item>
      </el-descriptions>
      <div v-else style="text-align: center; color: #999;">暂无数据</div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { getFeedbackList, getFeedbackDetail } from '../api/feedback'

const loading = ref(false)
const tableData = ref([])
const total = ref(0)

const queryParams = reactive({
  type: '',
  status: '',
  userType: '',
  clientType: '',
  keyword: '',
  page: 1,
  pageSize: 20
})

const detailVisible = ref(false)
const currentDetail = ref(null)
const imageUrls = ref([])

const handleSearch = () => {
  queryParams.page = 1
  loadData()
}

const handleReset = () => {
  queryParams.type = ''
  queryParams.status = ''
  queryParams.userType = ''
  queryParams.clientType = ''
  queryParams.keyword = ''
  queryParams.page = 1
  queryParams.pageSize = 20
  loadData()
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadData()
}

const handlePageChange = (page) => {
  queryParams.page = page
  loadData()
}

const loadData = async () => {
  loading.value = true
  try {
    const { data, code, msg, success } = await getFeedbackList(queryParams)
    if ((code && code !== '000000') || success === false) {
      throw new Error(msg || '获取反馈列表失败')
    }
    const pageData = data || {}
    tableData.value = pageData.records || []
    total.value = pageData.total || 0
  } catch (error) {
    console.error('获取反馈列表失败:', error)
    ElMessage.error(error.message || '获取反馈列表失败')
  } finally {
    loading.value = false
  }
}

const handleView = async (row) => {
  try {
    const { data, code, msg, success } = await getFeedbackDetail(row.id)
    if ((code && code !== '000000') || success === false) {
      throw new Error(msg || '获取反馈详情失败')
    }
    currentDetail.value = data || null
    if (currentDetail.value && currentDetail.value.attachments) {
      try {
        const arr = JSON.parse(currentDetail.value.attachments)
        imageUrls.value = Array.isArray(arr) ? arr : []
      } catch (e) {
        console.warn('解析反馈图片失败:', e)
        imageUrls.value = []
      }
    } else {
      imageUrls.value = []
    }
    detailVisible.value = true
  } catch (error) {
    console.error('获取反馈详情失败:', error)
    ElMessage.error(error.message || '获取反馈详情失败')
  }
}

const getTypeLabel = (type) => {
  switch (type) {
    case 'SUGGESTION': return '功能建议'
    case 'BUG': return '问题反馈'
    case 'DATA': return '数据异常'
    case 'OTHER': return '其他'
    default: return type || '-'
  }
}

const getStatusLabel = (status) => {
  switch (status) {
    case 'NEW': return '未处理'
    case 'PROCESSING': return '处理中'
    case 'DONE': return '已处理'
    default: return status || '-'
  }
}

const getStatusTagType = (status) => {
  switch (status) {
    case 'NEW': return 'danger'
    case 'PROCESSING': return 'warning'
    case 'DONE': return 'success'
    default: return 'info'
  }
}

const getUserTypeLabel = (userType) => {
  switch (userType) {
    case 'SALESMAN': return '业务员'
    case 'MANAGER': return '管理员'
    case 'ACCOUNTANT': return '会计'
    default: return userType || '-'
  }
}

const getClientTypeLabel = (clientType) => {
  switch (clientType) {
    case 'MINI_SALESMAN': return '业务员小程序'
    case 'MINI_MANAGER': return '管理员小程序'
    case 'PC': return 'PC端'
    default: return clientType || '-'
  }
}

// 格式化日期时间：年月日 时分秒
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-'
  try {
    const date = new Date(dateTimeStr)
    if (isNaN(date.getTime())) return dateTimeStr
    
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  } catch (e) {
    console.warn('格式化时间失败:', e, dateTimeStr)
    return dateTimeStr
  }
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
.feedback-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 16px;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.content-text {
  white-space: pre-wrap;
  line-height: 1.6;
}

.image-preview-list {
  display: flex;
  flex-wrap: wrap;
}

.desc-label {
  width: 90px;
}
</style>


